import React from "react";
import { Link } from "react-router-dom";
import {
  CodeBracketSquareIcon,
  CircleStackIcon,
  MagnifyingGlassCircleIcon,
  BuildingOffice2Icon,
  ScaleIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";

import { AGENT_COLORS } from "./BuilderField";

const SITE_EXAMPLES = [
  {
    kind: "data",
    title: "Directories",
    example: "Dog-friendly hotels in every US city",
    Icon: BuildingOffice2Icon,
  },
  {
    kind: "code",
    title: "Comparisons",
    example: "Espresso grinders under $500, side by side",
    Icon: ScaleIcon,
  },
  {
    kind: "seo",
    title: "Rankings",
    example: "Best hiking gear, scored and ranked",
    Icon: TrophyIcon,
  },
];

const FEATURES = [
  {
    kind: "code",
    title: "Site Builder",
    body: "Writes templates and components, then ships a live site for every row.",
    Icon: CodeBracketSquareIcon,
  },
  {
    kind: "data",
    title: "Data Agent",
    body: "Researches sources, cleans tables, and keeps the dataset behind every page fresh.",
    Icon: CircleStackIcon,
  },
  {
    kind: "seo",
    title: "SEO / UX Audits",
    body: "Checks titles, schema, links, speed, and tap targets on every generated page.",
    Icon: MagnifyingGlassCircleIcon,
  },
];

const Intro = () => {
  return (
    <div>
      <h1 className="text-3xl font-bold leading-tight">
        SEO sites, built from data, <span style={{ color: AGENT_COLORS.code }}>on autopilot.</span>
      </h1>
      <p className="mt-3 text-[15px] leading-6 opacity-70">
        Weave turns a dataset into a search-optimized website, with a page for every entry.
      </p>

      <h2 className="mb-3 mt-8 text-sm font-semibold">Sites that work well</h2>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {SITE_EXAMPLES.map((item) => {
          const Icon = item.Icon;
          return (
            <div key={item.title} className="flex gap-3 rounded-lg border border-theme-secondary p-3">
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                style={{ background: `${AGENT_COLORS[item.kind]}22`, color: AGENT_COLORS[item.kind] }}
              >
                <Icon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold">{item.title}</p>
                <p className="text-xs leading-4 opacity-60">{item.example}</p>
              </div>
            </div>
          );
        })}
      </div>

      <h2 className="mb-3 mt-8 text-sm font-semibold">What you get</h2>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {FEATURES.map((item) => {
          const Icon = item.Icon;
          return (
            <div key={item.title} className="rounded-lg border border-theme-secondary p-3.5">
              <div className="flex items-center gap-2.5">
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                  style={{ background: `${AGENT_COLORS[item.kind]}22`, color: AGENT_COLORS[item.kind] }}
                >
                  <Icon className="h-4 w-4" />
                </span>
                <p className="text-sm font-semibold">{item.title}</p>
              </div>
              <p className="mt-2 text-xs leading-4 opacity-60">{item.body}</p>
            </div>
          );
        })}
      </div>

      <Link
        to="/onboarding/brief/"
        className="mt-8 block w-full rounded-lg bg-theme-primary py-3 text-center font-semibold text-theme-button"
      >
        Start building
      </Link>
    </div>
  );
};

export default Intro;
